import { useEffect, useMemo } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import { SimpleGrid, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ContractsHeader } from "@/components/molecules/SubHeader";
import { postApproveContract, prefetchContractsLight, useGetContractsLight } from "@/services/Contracts";
import { ContractLight, ContractLightSearch, CreateApproval } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { stripTime } from "@/utils/Dates";
import { getContractParties } from "@/utils/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import { useOpenCreateContract } from "@/hooks/useGuardedWizards";
import { useTradeLock } from "@/hooks/useTradeLock";
import { useContractStore } from "@/stores/contractStore";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";

import dayjs from "dayjs";
import CreateContractCta from "../../molecules/CreateContractCta";
import ContractList from "./ContractList";
import ContractsFilter from "@/components/organisms/ContractsFilter";
import ContractCard from "@/components/organisms/ContractCard";
import BulkContractUpdate from "@/components/organisms/BulkContractUpdate";
import styles from "./styles.module.css";

export const Contracts = () => {
  const { t } = useTranslation();

  const { currentCompany, user, isViewer } = useAuth();
  const queryClient = useQueryClient();
  const open = useOpenCreateContract();
  const { guardTradeLock } = useTradeLock();
  const showListAsGrid = useContractStore((state) => state.showListAsGrid);
  const sortOption = useContractStore((state) => state.sortOption);
  const sortDirection = useContractStore((state) => state.sortDirection);
  const statuses = useContractStore((state) => state.statuses);
  const query = useContractStore((state) => state.query);
  const [debouncedQuery] = useDebouncedValue(query, 400);
  const limit = useContractStore((state) => state.limit);
  const currentPage = useContractStore((state) => state.currentPage);
  const setNumberOfPages = useContractStore((state) => state.setNumberOfPages);

  const {
    data: searchResults,
    isError: isErrorFetchingContracts,
    isLoading: isLoadingContracts,
    isFetching: isFetchingContracts,
  } = useGetContractsLight({
    sortOption: sortOption,
    sortDirection: sortDirection,
    limit: limit,
    showDeleted: false,
    statuses: statuses,
    pageNumber: currentPage,
    query: debouncedQuery,
  });

  useEffect(() => {
    if (!searchResults) return;
    setNumberOfPages(searchResults?.numberOfContracts ? Math.ceil(searchResults.numberOfContracts / limit) : 0);
  }, [searchResults]);

  useEffect(() => {
    if (!searchResults) return;
    if (currentPage < searchResults.numberOfPages) {
      prefetchContractsLight(queryClient, {
        sortOption,
        sortDirection,
        limit,
        showDeleted: false,
        statuses,
        pageNumber: currentPage + 1,
        query: debouncedQuery,
      });
    }
  }, [searchResults, currentPage, sortOption, sortDirection, limit, statuses, debouncedQuery, queryClient]);

  useEffect(() => {
    if (isErrorFetchingContracts) {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.fetchError"), t("notifications:contracts.fetchError"))
      );
    }
  }, [isErrorFetchingContracts]);

  const { mutateAsync: mutateApproveContract, isPending: isPendingMutateApproval } = useMutation({
    mutationFn: async (contractId: number) => {
      const approvalBody: CreateApproval = {
        approvalDate: stripTime(dayjs().toDate()),
        approvedBy: user?.id as number,
        approvalCompany: currentCompany?.companyId as number,
        contractId: contractId,
      };
      await postApproveContract(contractId, approvalBody);
    },
    onMutate: async (contractId: number) => {
      await queryClient.cancelQueries({ queryKey: ["getContractsLight"] });
      const snapshots = queryClient.getQueriesData<ContractLightSearch>({ queryKey: ["getContractsLight"] });
      const optimisticApproval = {
        id: -1,
        approvalDate: stripTime(dayjs().toDate()).toISOString(),
        approvedBy: user?.id as number,
        approvalCompany: currentCompany?.companyId as number,
        contractId,
      };
      queryClient.setQueriesData<ContractLightSearch>({ queryKey: ["getContractsLight"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          contracts: old.contracts.map((c) =>
            c.id === contractId ? { ...c, approvals: [...c.approvals, optimisticApproval] } : c
          ),
        };
      });
      return { snapshots };
    },
    onError: (_err, _contractId, context) => {
      context?.snapshots?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["getContractsLight"] });
    },
  });

  // Memoised: ContractList feeds this into a useMemo dependency array for its table column defs. Takes the whole
  // contract rather than its id so the guard can see the counterparty.
  const approveContract = useMemo(
    () => guardTradeLock((contract: ContractLight) => mutateApproveContract(contract.id), getContractParties),
    [guardTradeLock, mutateApproveContract]
  );

  return (
    <>
      <title>{`Reviver - ${t("contracts:contracts")}`}</title>
      <meta name="description" content={`Reviver - ${t("contracts:contracts")}`} />

      <div>
        <ContractsHeader
          companyName={currentCompany?.companyName ?? ""}
          totalContracts={searchResults?.numberOfContracts ?? 0}
          sortOptions={[]}
          status={statuses[0]}
          isFetchingContracts={isFetchingContracts}
          onClickNewContract={() => open()}
        />
        <div className={styles.root}>
          <div className={styles.activeContracts}>
            <ContractsFilter />
            {showListAsGrid && (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing={{ base: "sm", sm: "md", lg: "lg" }} pt="15px">
                {!isViewer(currentCompany) && <CreateContractCta onClick={() => open()} />}
                {isLoadingContracts && (
                  <>
                    {new Array(limit).fill(0).map((_, i) => (
                      <Skeleton key={i} height="100px" width={"100%"} />
                    ))}
                  </>
                )}
                {searchResults?.contracts &&
                  searchResults?.contracts.length > 0 &&
                  searchResults?.contracts?.map((contract: ContractLight, i: number) => (
                    <ContractCard
                      contract={contract}
                      approveContract={() => approveContract(contract)}
                      isPendingMutateApproval={isPendingMutateApproval || isFetchingContracts}
                      key={i}
                    />
                  ))}
              </SimpleGrid>
            )}
            {!showListAsGrid && searchResults?.contracts && (
              <ContractList
                contracts={searchResults?.contracts}
                isLoading={isLoadingContracts}
                totalItems={searchResults?.numberOfContracts ?? 0}
                onClickApproveContract={approveContract}
                isPendingApproval={isPendingMutateApproval || isFetchingContracts}
              />
            )}
          </div>
        </div>
      </div>
      <BulkContractUpdate />
    </>
  );
};

export default Contracts;
